import {
  InternalOpsChannel,
  useInternalOpsChannels,
} from "@/lib/api/internal-ops-messaging";
import { useCurrentUser, useGetUserSupervisorAndChannel } from "@/lib/api/user";
import { useFavoriteChannelUrls } from "@/lib/hooks/conversations/use-support-chat-favorites-store";
import { useSignedMedia } from "@/lib/hooks/use-signed-media";
import { PostHogEvents, useTrackEvent } from "@/lib/posthog-events";
import { cn, getInitials } from "@/lib/utils";
import { classifySupportChannel } from "@/lib/utils/support-channels";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { GroupChannel } from "@sendbird/chat/groupChannel";
import useSendbirdStateContext from "@sendbird/uikit-react/useSendbirdStateContext";
import { Moodies } from "@somethings-ui/core/assets";
import { useMemo } from "react";
import { ChatListSection } from "./chat-list-section";
import { isMentorIdentity } from "@/lib/utils/system-roles";

export type SupportChannelSelection = {
  sdkUrl: string;
  internalChannelId: string | null;
};

interface SupportChannelListProps {
  channels: GroupChannel[];
  loading: boolean;
  currentChannelUrl: string | null;
  searchQuery?: string;
  onSelectChannel: (selection: SupportChannelSelection) => void;
}

export function SupportChannelList({
  channels,
  loading,
  currentChannelUrl,
  searchQuery = "",
  onSelectChannel,
}: SupportChannelListProps) {
  const { track } = useTrackEvent();
  const sb = useSendbirdStateContext();
  const myUserId = sb.config.userId;
  const { data: internalChannels } = useInternalOpsChannels();
  const { data: currentUser } = useCurrentUser();

  const systemRoles = currentUser?.systemRoles ?? [];
  const isCurrentUserMentor = isMentorIdentity(systemRoles);

  const { data: supervisorData } = useGetUserSupervisorAndChannel(
    currentUser?.id ?? "",
    !!currentUser?.id && isCurrentUserMentor,
  );
  const activeSupervisorId = supervisorData?.supervisor?.id ?? null;

  const favoriteUrls = useFavoriteChannelUrls(currentUser?.id);

  const internalByUrl = useMemo(
    () => new Map((internalChannels ?? []).map((c) => [c.sdk_url, c])),
    [internalChannels],
  );

  const visibleChannels = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return channels ?? [];
    return (channels ?? []).filter((channel) =>
      supportChannelTitle(channel, internalByUrl.get(channel.url), myUserId)
        .toLowerCase()
        .includes(query),
    );
  }, [channels, internalByUrl, myUserId, searchQuery]);

  const buckets = useMemo(() => {
    const list = visibleChannels;
    const favorites: GroupChannel[] = [];
    const mentors: GroupChannel[] = [];
    const leadMentors: GroupChannel[] = [];
    const supervisors: GroupChannel[] = [];
    const admins: GroupChannel[] = [];
    const cohorts: GroupChannel[] = [];
    const groups: GroupChannel[] = [];
    const other: GroupChannel[] = [];

    const ctx = {
      currentUserId: myUserId,
      isCurrentUserMentor,
      activeSupervisorId,
    };

    for (const channel of list) {
      if (!channel.customType?.startsWith("INTERNAL_")) {
        continue;
      }
      // Frozen channels are excluded from the reload fetch (includeFrozen:false),
      // so skip them here too, keeping the live list consistent with a reload.
      if (channel.isFrozen) {
        continue;
      }
      // Favorited channels also appear in Favorites, but stay in their normal
      // section too (no `continue`) so favoriting doesn't remove them from it.
      if (favoriteUrls.has(channel.url)) {
        favorites.push(channel);
      }
      const bucket = classifySupportChannel(
        internalByUrl.get(channel.url),
        ctx,
      );
      switch (bucket) {
        case "cohorts":
          cohorts.push(channel);
          break;
        case "groups":
          groups.push(channel);
          break;
        case "supervisors":
          supervisors.push(channel);
          break;
        case "mentors":
          mentors.push(channel);
          break;
        case "leadMentors":
          leadMentors.push(channel);
          break;
        case "admins":
          admins.push(channel);
          break;
        case "other":
          other.push(channel);
          break;
        default: {
          const exhaustiveCheck: never = bucket;
          return exhaustiveCheck;
        }
      }
    }

    return {
      favorites,
      mentors,
      leadMentors,
      supervisors,
      admins,
      cohorts,
      groups,
      other,
    };
  }, [
    visibleChannels,
    internalByUrl,
    favoriteUrls,
    myUserId,
    isCurrentUserMentor,
    activeSupervisorId,
  ]);

  const renderRow = (channel: GroupChannel) => {
    const internal = internalByUrl.get(channel.url) ?? null;
    return (
      <ChannelRow
        key={channel.url}
        channel={channel}
        internal={internal}
        isActive={currentChannelUrl === channel.url}
        onSelect={() =>
          onSelectChannel({
            sdkUrl: channel.url,
            internalChannelId: internal?.id ?? null,
          })
        }
      />
    );
  };

  const onCategoryToggle = (category: string, count: number) =>
    track(PostHogEvents.OPS_CHAT_CATEGORY_EXPAND, { category, count });

  const unreadCount = (list: GroupChannel[]) =>
    list.filter((c) => c.unreadMessageCount > 0).length;

  const searching = searchQuery.trim().length > 0;

  const renderSection = (
    title: string,
    list: GroupChannel[],
    category: string,
    options?: { always?: boolean; defaultOpen?: boolean },
  ) => {
    if (list.length === 0 && (searching || !options?.always)) return null;
    return (
      <ChatListSection
        key={category}
        title={title}
        badgeCount={unreadCount(list)}
        defaultOpen={options?.defaultOpen ?? false}
        loading={loading}
        isEmpty={list.length === 0}
        onToggle={(open) => open && onCategoryToggle(category, list.length)}
      >
        {list.map(renderRow)}
      </ChatListSection>
    );
  };

  return (
    <div className="pt-1.5">
      {renderSection("Favorites", buckets.favorites, "favorites", {
        defaultOpen: true,
      })}
      {renderSection("Mentors", buckets.mentors, "mentors", {
        defaultOpen: true,
      })}
      {renderSection("Lead mentors", buckets.leadMentors, "lead_mentors", {
        defaultOpen: true,
      })}
      {renderSection("Supervisors", buckets.supervisors, "supervisors", {
        always: true,
        defaultOpen: true,
      })}
      {renderSection("Admins", buckets.admins, "admins")}
      {renderSection("Cohorts", buckets.cohorts, "cohorts", { always: true })}
      {renderSection("Groups", buckets.groups, "groups", { always: true })}
      {renderSection("Other", buckets.other, "other", { always: true })}
    </div>
  );
}

function supportChannelTitle(
  channel: GroupChannel,
  internal: InternalOpsChannel | null | undefined,
  myUserId: string,
): string {
  if (internal && internal.kind !== "direct" && internal.channel_display_name) {
    return internal.channel_display_name;
  }
  const otherMembers = channel.members.filter(
    (member) => member.userId !== myUserId,
  );
  return (
    otherMembers.map((member) => member.nickname || member.userId).join(", ") ||
    channel.name ||
    "Untitled"
  );
}

function ChannelRow({
  channel,
  internal,
  isActive,
  onSelect,
}: {
  channel: GroupChannel;
  internal: InternalOpsChannel | null;
  isActive: boolean;
  onSelect: () => void;
}) {
  const sb = useSendbirdStateContext();
  const myUserId = sb.config.userId;
  const { track } = useTrackEvent();

  const otherMembers = channel.members.filter((m) => m.userId !== myUserId);
  const title = supportChannelTitle(channel, internal, myUserId);

  const lastMessage = channel.lastMessage as
    | { message?: string; url?: string; createdAt?: number }
    | undefined
    | null;
  const lastText =
    lastMessage?.message ?? (lastMessage?.url ? "[file]" : "No messages");

  const lastTime = lastMessage?.createdAt
    ? new Date(lastMessage.createdAt).toLocaleString("en-US", {
        hour: "numeric",
        minute: "numeric",
        hour12: true,
      })
    : "";

  const primaryMember = otherMembers[0];
  const avatarName = primaryMember?.nickname || primaryMember?.userId || title;
  const { url: avatarUrl } = useSignedMedia(
    primaryMember?.profileUrl ?? undefined,
  );

  return (
    <button
      type="button"
      onClick={() => {
        track(PostHogEvents.OPS_CHAT_THREAD_OPEN, {
          thread_id: channel.url,
          thread_type: otherMembers.length === 1 ? "user" : "group",
        });
        onSelect();
      }}
      className={cn(
        "flex w-full cursor-pointer flex-row items-center gap-2 px-4 py-3 text-left hover:bg-surfaces-z4",
        isActive && "bg-surfaces-z4",
      )}
    >
      <div
        className={cn(
          "size-2 shrink-0 rounded-full",
          channel.unreadMessageCount > 0 ? "bg-blue-600" : "bg-transparent",
        )}
      />
      <div className="flex size-11 shrink-0 items-center justify-center">
        {internal?.kind === "cohort" ? (
          <img
            src={Moodies.happy}
            alt="Cohort"
            className="size-10 object-contain"
          />
        ) : (
          <Avatar className="size-10 shrink-0">
            <AvatarImage src={avatarUrl} alt={avatarName} />
            <AvatarFallback className="bg-zinc-200 text-sm text-zinc-500">
              {getInitials(avatarName)}
            </AvatarFallback>
          </Avatar>
        )}
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex items-center justify-between gap-2">
          <span className="truncate text-sm font-bold text-black">{title}</span>
          <span className="shrink-0 text-xs text-[#a1a1aa]">{lastTime}</span>
        </div>
        <span className="truncate text-xs text-[#78716c]">{lastText}</span>
      </div>
    </button>
  );
}

// Mark unused props as intentional so TS doesn't complain on the export shape.
export type { InternalOpsChannel };
